import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, GripVertical, Save, Image, ChevronDown, ChevronUp, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { uploadWithFeedback } from '../../utils/cloudinaryUpload';
import FocalPointPicker from '../../components/FocalPointPicker';

const SECTION_CONFIG = {
  hero_banners: {
    label: 'Hero Banners',
    description: 'Main slideshow banners at the top of homepage',
    fields: ['image', 'title', 'subtitle', 'link', 'buttonText'],
    autoLink: false,
  },
  categories: {
    label: 'Shop by Category',
    description: 'Category tiles — adding here also adds to product form dropdown',
    fields: ['image', 'name'],
    autoLink: true,
  },
  collections: {
    label: 'Collections',
    description: 'Collection showcase — adding here also adds to product form dropdown',
    fields: ['image', 'name', 'subtitle'],
    autoLink: true,
  },
  explore_jewellery: {
    label: 'Explore Jewellery',
    description: 'Explore by metal type section',
    fields: ['image', 'name', 'featured'],
    autoLink: true,
  },
  gender_categories: {
    label: 'Gender Categories',
    description: 'Shop by gender section',
    fields: ['image', 'name'],
    autoLink: true,
  },
};

function SectionEditor({ sectionType, config }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [editingIndex, setEditingIndex] = useState(null);
  const [uploading, setUploading] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get(`/homepage/sections/${sectionType}`);
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(`Failed to load ${config.label}`);
    } finally {
      setLoading(false);
    }
  }, [sectionType, config.label]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const saveAll = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/homepage/sections/${sectionType}`, { items });
      toast.success(`${config.label} saved successfully`);
      setEditingIndex(null);
      // Refresh items from server to stay in sync
      if (res.data?.items) {
        setItems(res.data.items);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to save';
      toast.error(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const addItem = () => {
    const newItem = { id: '', name: '', image: '', link: '', sort_order: items.length };
    if (config.fields.includes('title')) newItem.title = '';
    if (config.fields.includes('subtitle')) newItem.subtitle = '';
    if (config.fields.includes('buttonText')) newItem.buttonText = '';
    if (config.fields.includes('featured')) newItem.featured = false;
    setItems([...items, newItem]);
    setEditingIndex(items.length);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
    if (editingIndex === index) setEditingIndex(null);
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const moveItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    const updated = [...items];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated.forEach((item, i) => item.sort_order = i);
    setItems(updated);
  };

  const handleImageUpload = async (index, file) => {
    setUploading(index);
    try {
      const result = await uploadWithFeedback(file, 'homepage', toast);
      if (result?.url) {
        const updated = [...items];
        updated[index] = { ...updated[index], image: result.url };
        setItems(updated);
        // Auto-save after image upload
        setSaving(true);
        try {
          const res = await api.put(`/homepage/sections/${sectionType}`, { items: updated });
          toast.success('Image updated and saved');
          if (res.data?.items) setItems(res.data.items);
        } catch (err) {
          toast.error('Image uploaded but save failed. Click Save to retry.');
        } finally {
          setSaving(false);
        }
      }
    } catch (err) {
      toast.error('Image upload failed');
    } finally {
      setUploading(null);
    }
  };

  if (loading) return <div className="p-4 text-gray-500">Loading {config.label}...</div>;

  return (
    <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden" data-testid={`section-${sectionType}`}>
      <div
        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div>
          <h3 className="font-semibold text-[#1a3a5c] text-lg">{config.label}</h3>
          <p className="text-sm text-gray-500">{config.description} ({items.length} items)</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); saveAll(); }}
            disabled={saving}
            className="px-3 py-1.5 bg-[#1a3a5c] text-white rounded text-sm hover:bg-[#0d2240] disabled:opacity-50 flex items-center gap-1"
            data-testid={`save-${sectionType}`}
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? 'Saving...' : 'Save'}
          </button>
          {expanded ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-3">
          {items.map((item, index) => (
            <div key={item.id || `item-${index}`} className="border border-gray-200 rounded-lg p-3 bg-white">
              <div className="flex items-start gap-3">
                {/* Thumbnail */}
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 relative">
                  {item.image ? (
                    <img src={item.image} alt={item.name || item.title || ''} className="w-full h-full object-cover" style={(item.focal_x != null || item.focal_y != null) ? { objectPosition: `${item.focal_x ?? 50}% ${item.focal_y ?? 50}%` } : undefined} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <Image className="h-8 w-8" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm text-[#1a3a5c] truncate">
                      {item.name || item.title || `Item ${index + 1}`}
                    </span>
                    <span className="text-xs text-gray-400">#{index + 1}</span>
                  </div>
                  {editingIndex !== index ? (
                    <p className="text-xs text-gray-500 truncate">{item.link || (config.autoLink ? 'Link auto-generated on save' : 'No link set')}</p>
                  ) : null}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => moveItem(index, -1)} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30" title="Move up">
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button onClick={() => moveItem(index, 1)} disabled={index === items.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30" title="Move down">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button onClick={() => setEditingIndex(editingIndex === index ? null : index)} className="p-1 text-blue-500 hover:text-blue-700" title="Edit">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => removeItem(index)} className="p-1 text-red-400 hover:text-red-600" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Edit Form */}
              {editingIndex === index && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                  {/* Image Upload */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Image</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={item.image || ''}
                        onChange={(e) => updateItem(index, 'image', e.target.value)}
                        placeholder="Image URL or upload below"
                        className="flex-1 px-2 py-1.5 border rounded text-sm"
                      />
                      <label className={`px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm cursor-pointer hover:bg-gray-200 ${uploading === index ? 'opacity-50' : ''}`}>
                        {uploading === index ? 'Uploading...' : 'Upload'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={uploading === index}
                          onChange={(e) => e.target.files[0] && handleImageUpload(index, e.target.files[0])}
                        />
                      </label>
                    </div>
                  </div>

                  {/* Focal Point Picker */}
                  {item.image && (
                    <FocalPointPicker
                      src={item.image}
                      focalX={item.focal_x ?? 50}
                      focalY={item.focal_y ?? 50}
                      onChange={({ x, y }) => {
                        const updated = [...items];
                        updated[index] = { ...updated[index], focal_x: x, focal_y: y };
                        setItems(updated);
                      }}
                    />
                  )}

                  {config.fields.includes('name') && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                      <input type="text" value={item.name || ''} onChange={(e) => updateItem(index, 'name', e.target.value)} placeholder="Category name" className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                  )}
                  {config.fields.includes('title') && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                      <input type="text" value={item.title || ''} onChange={(e) => updateItem(index, 'title', e.target.value)} placeholder="Banner title" className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                  )}
                  {config.fields.includes('subtitle') && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Subtitle</label>
                      <input type="text" value={item.subtitle || ''} onChange={(e) => updateItem(index, 'subtitle', e.target.value)} placeholder="Subtitle text" className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                  )}
                  {config.fields.includes('buttonText') && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Button Text</label>
                      <input type="text" value={item.buttonText || ''} onChange={(e) => updateItem(index, 'buttonText', e.target.value)} placeholder="e.g. Shop Gold" className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                  )}
                  {config.fields.includes('link') && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Link</label>
                      <input type="text" value={item.link || ''} onChange={(e) => updateItem(index, 'link', e.target.value)} placeholder="/products?category=gold" className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                  )}
                  {config.autoLink && (
                    <p className="text-xs text-green-600 mt-1">Link auto-generated from name. Also synced to product form dropdown.</p>
                  )}
                  {config.fields.includes('featured') && (
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={item.featured || false} onChange={(e) => updateItem(index, 'featured', e.target.checked)} />
                      Featured
                    </label>
                  )}
                </div>
              )}
            </div>
          ))}

          <button
            onClick={addItem}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:text-[#1a3a5c] hover:border-[#1a3a5c] transition-colors flex items-center justify-center gap-2 text-sm"
            data-testid={`add-${sectionType}`}
          >
            <Plus className="h-4 w-4" />
            Add New Item
          </button>
        </div>
      )}
    </div>
  );
}

export default function HomepageTab() {
  return (
    <div className="space-y-2" data-testid="homepage-tab">
      <div className="mb-4">
        <h2 className="text-2xl font-serif font-bold text-[#1a3a5c]">Homepage Management</h2>
        <p className="text-gray-500 text-sm">Manage all images and content displayed on your homepage</p>
      </div>
      {Object.entries(SECTION_CONFIG).map(([key, config]) => (
        <SectionEditor key={key} sectionType={key} config={config} />
      ))}
    </div>
  );
}
